/** @jsxImportSource theme-ui */
import { useAuthentication } from '@/providers/AuthenticationProvider';
import { useActor } from '@xstate/react';
import { Routes, Route } from 'react-router-dom';
import Companies from '../containers/companies/Companies';
import Bookings from '../containers/bookings/Bookings';
import CompanyDetails from '../containers/companyDetails/CompanyDetails';
import TicketsPage from './TicketsPage';
import CreateTicketPage from './CreateTicketPage';
import EditTicketPage from './EditTicketPage';
import CustomersPage from './CustomersPage';
import EditBillingInformationPage from './EditBillingInformationPage';
import CompanyServicesArticlesPage from './CompanyServicesArticlesPage';
import CompanyEmbedPage from './CompanyEmbedPage';
import ApiKeys from './ApiKeys';
import { getIsAuthenticated } from '@/machines/authMachine';
import ProtectedRoute from '@/components/ProtectedRoute';
import FavoriteActions from '@/containers/favoriteActions/FavoriteActions';
import CreateIncentivePage from './CreateIncentivePage';
import EditIncentivePage from './EditIncentivePage';
import Incentives from './IncentivesPage';
import Actions from './ActionsPage';
import CreateActionPage from './CreateActionPage';
import Balance from './VossBalancePage';
import VossInvoicesPage from './VossInvoicesPage';
import VossUsage from './VossUsagePage';
import EditActionPage from './EditActionPage';
import CreateUsagePage from './CreateUsagePage';
import EditDiscountAgreementPage from './EditDiscountAgreementPage';
import CreateDiscountAgreementPage from './CreateDiscountAgreementPage';
import DiscountAgreementsPage from './DiscountAgreementsPage';
import ConnectDiscountAgreementPage from './ConnectDiscountAgreementPage';
import CreateVossInvoiceJobPage from './CreateVossInvoiceJobPage';
import VossErrorLogsPage from './VossErrorLogsPage';
import VossToolsPage from './VossToolsPage';
import VossUsageAuditPage from './VossUsageAuditPage';
import VossSubscriptionAuditPage from './VossSubscriptionAuditPage';
import LicenseChangesLogPage from './LicenseChangesLogPage';
import MailchimpPage from './MailchimpPage';
import NewsletterBroadcastPage from './NewsletterBroadcastPage';
import WidgetConfigurationSchemaPage from './WidgetConfigurationSchemaPage';
import WidgetConfigurationsPage from './WidgetConfigurationsPage';
import FeatureFlagsPage from './FeatureFlagsPage';
import AssistantUsagePage from './AssistantUsagePage';
import RedirectDomainsPage from './RedirectDomainsPage';
import CompanyDeletionSchedulesPage from './CompanyDeletionSchedulesPage';

const Full: React.FC = () => {
    // @ts-ignore
    const { authService } = useAuthentication();
    const authActor = useActor(authService as any);
    const authState: any = authActor[0];
    const isAuthenticated = getIsAuthenticated(authState);

    return (
        <>
            <Routes>
                <Route
                    key="home"
                    path="/"
                    element={
                        <ProtectedRoute authenticated={isAuthenticated}>
                            <FavoriteActions />
                        </ProtectedRoute>
                    }
                />
                <Route
                    key="/incentives"
                    path="/incentives"
                    element={
                        <ProtectedRoute authenticated={isAuthenticated}>
                            <Incentives />
                        </ProtectedRoute>
                    }
                />
                <Route
                    key="/actions"
                    path="/actions"
                    element={
                        <ProtectedRoute authenticated={isAuthenticated}>
                            <Actions />
                        </ProtectedRoute>
                    }
                />
                <Route
                    key="/actions/new"
                    path="/actions/new"
                    element={
                        <ProtectedRoute authenticated={isAuthenticated}>
                            <CreateActionPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    key="/clients/:companyId/createusage"
                    path="/clients/:companyId/createusage"
                    element={
                        <ProtectedRoute authenticated={isAuthenticated}>
                            <CreateUsagePage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    key="/voss/discount-agreements/new"
                    path="/voss/discount-agreements/new"
                    element={
                        <ProtectedRoute authenticated={isAuthenticated}>
                            <CreateDiscountAgreementPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    key="/voss/discount-agreements/:id"
                    path="/voss/discount-agreements/:id"
                    element={
                        <ProtectedRoute authenticated={isAuthenticated}>
                            <EditDiscountAgreementPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    key="/voss/discount-agreements/:id/connect"
                    path="/voss/discount-agreements/:id/connect"
                    element={
                        <ProtectedRoute authenticated={isAuthenticated}>
                            <ConnectDiscountAgreementPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    key="/voss/discount-agreements"
                    path="/voss/discount-agreements"
                    element={
                        <ProtectedRoute authenticated={isAuthenticated}>
                            <DiscountAgreementsPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    key="/actions/:id"
                    path="/actions/:id"
                    element={
                        <ProtectedRoute authenticated={isAuthenticated}>
                            <EditActionPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    key="/clients/:companyId/incentives/:incentiveId"
                    path="/clients/:companyId/incentives/:incentiveId"
                    element={
                        <ProtectedRoute authenticated={isAuthenticated}>
                            <CreateIncentivePage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    key="/incentives/new"
                    path="/incentives/new"
                    element={
                        <ProtectedRoute authenticated={isAuthenticated}>
                            <CreateIncentivePage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    key="/incentives/:incentiveId"
                    path="/incentives/:incentiveId"
                    element={
                        <ProtectedRoute authenticated={isAuthenticated}>
                            <EditIncentivePage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    key="/clients"
                    path="/clients"
                    element={
                        <ProtectedRoute authenticated={isAuthenticated}>
                            <Companies />
                        </ProtectedRoute>
                    }
                />
                <Route
                    key="/customers"
                    path="/customers"
                    element={
                        <ProtectedRoute authenticated={isAuthenticated}>
                            <CustomersPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    key="/clients/:id"
                    path="/clients/:id"
                    element={
                        <ProtectedRoute authenticated={isAuthenticated}>
                            <CompanyDetails />
                        </ProtectedRoute>
                    }
                />
                <Route
                    key="/bookings"
                    path="/bookings"
                    element={
                        <ProtectedRoute authenticated={isAuthenticated}>
                            <Bookings />
                        </ProtectedRoute>
                    }
                />
                <Route
                    key="/tickets"
                    path="/tickets"
                    element={
                        <ProtectedRoute authenticated={isAuthenticated}>
                            <TicketsPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    key="/tickets/new"
                    path="/tickets/new"
                    element={
                        <ProtectedRoute authenticated={isAuthenticated}>
                            <CreateTicketPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    key="/clients/:companyId/tickets/new"
                    path="/clients/:companyId/tickets/new"
                    element={
                        <ProtectedRoute authenticated={isAuthenticated}>
                            <CreateTicketPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    key="/tickets/:ticketId"
                    path="/tickets/:ticketId"
                    element={
                        <ProtectedRoute authenticated={isAuthenticated}>
                            <EditTicketPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    key="/apikeys"
                    path="/apikeys/:id"
                    element={
                        <ProtectedRoute authenticated={isAuthenticated}>
                            <ApiKeys />
                        </ProtectedRoute>
                    }
                />
                <Route
                    key="/mailchimp"
                    path="/mailchimp"
                    element={
                        <ProtectedRoute authenticated={isAuthenticated}>
                            <MailchimpPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    key="/newsletter"
                    path="/newsletter"
                    element={
                        <ProtectedRoute authenticated={isAuthenticated}>
                            <NewsletterBroadcastPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    key="/widget/configuration-schemas"
                    path="/widget/configuration-schemas"
                    element={
                        <ProtectedRoute authenticated={isAuthenticated}>
                            <WidgetConfigurationSchemaPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    key="/clients/:companyId/widget/configurations"
                    path="/clients/:companyId/widget/configurations"
                    element={
                        <ProtectedRoute authenticated={isAuthenticated}>
                            <WidgetConfigurationsPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    key="/feature-flags"
                    path="/feature-flags"
                    element={
                        <ProtectedRoute authenticated={isAuthenticated}>
                            <FeatureFlagsPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    key="/assistant-usage"
                    path="/assistant-usage"
                    element={
                        <ProtectedRoute authenticated={isAuthenticated}>
                            <AssistantUsagePage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    key="/redirect-domains"
                    path="/redirect-domains"
                    element={
                        <ProtectedRoute authenticated={isAuthenticated}>
                            <RedirectDomainsPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    key="/voss/balance"
                    path="/voss/balance"
                    element={
                        <ProtectedRoute authenticated={isAuthenticated}>
                            <Balance />
                        </ProtectedRoute>
                    }
                />
                <Route
                    key="/voss/invoices"
                    path="/voss/invoices"
                    element={
                        <ProtectedRoute authenticated={isAuthenticated}>
                            <VossInvoicesPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    key="/voss/invoice/new"
                    path="/voss/invoice/new"
                    element={
                        <ProtectedRoute authenticated={isAuthenticated}>
                            <CreateVossInvoiceJobPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    key="/voss/usage"
                    path="/voss/usage"
                    element={
                        <ProtectedRoute authenticated={isAuthenticated}>
                            <VossUsage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    key="/voss/usage-audit"
                    path="/voss/usage-audit"
                    element={
                        <ProtectedRoute authenticated={isAuthenticated}>
                            <VossUsageAuditPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    key="/voss/subscription-audit"
                    path="/voss/subscription-audit"
                    element={
                        <ProtectedRoute authenticated={isAuthenticated}>
                            <VossSubscriptionAuditPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    key="/voss/tools"
                    path="/voss/tools"
                    element={
                        <ProtectedRoute authenticated={isAuthenticated}>
                            <VossToolsPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    key="/errorlogs"
                    path="/errorlogs"
                    element={
                        <ProtectedRoute authenticated={isAuthenticated}>
                            <VossErrorLogsPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    key="/deletion-schedules"
                    path="/deletion-schedules"
                    element={
                        <ProtectedRoute authenticated={isAuthenticated}>
                            <CompanyDeletionSchedulesPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    key="/licensechangeslog"
                    path="/licensechangeslog"
                    element={
                        <ProtectedRoute authenticated={isAuthenticated}>
                            <LicenseChangesLogPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    key="/clients/:companyId/billing"
                    path="/clients/:companyId/billing"
                    element={
                        <ProtectedRoute authenticated={isAuthenticated}>
                            <EditBillingInformationPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    key="/clients/:companyId/services-articles"
                    path="/clients/:companyId/services-articles"
                    element={
                        <ProtectedRoute authenticated={isAuthenticated}>
                            <CompanyServicesArticlesPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    key="/clients/:companyId/embed"
                    path="/clients/:companyId/embed"
                    element={
                        <ProtectedRoute authenticated={isAuthenticated}>
                            <CompanyEmbedPage />
                        </ProtectedRoute>
                    }
                />
            </Routes>
        </>
    );
};

export default Full;
